import React, { type ReactElement, useMemo, useCallback } from "react";
import { Button, Form, Modal, type FormInstance } from "antd";
import { type ModalProps } from "antd/lib/modal";
import { useTranslation } from "react-i18next";

interface OwnProps {
  loading?: boolean;
  children: ReactElement<{ formController: FormInstance }>;
  onSave?: () => void;
}

type Props = OwnProps & ModalProps & { visible?: boolean };

function ModalForm<T extends object>({ children, loading, open, visible, onSave, ...restProps }: Props) {
  const { t } = useTranslation("common");
  const [formController] = Form.useForm<T>();

  const onClose = useCallback((e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    formController.resetFields();
    restProps.onCancel?.(e as React.MouseEvent<HTMLButtonElement, MouseEvent>);
  }, [formController, restProps.onCancel]);

  const afterClose = useCallback(() => {
    formController.resetFields();
  }, [formController]);

  const handleSubmit = useCallback(() => {
    if (onSave) {
      onSave();
    } else {
      formController.submit();
    }
  }, [onSave, formController]);

  const footer = useMemo(() => [
    <Button key="back" onClick={onClose} disabled={loading}>
      {restProps.cancelText || t<string>("button.cancel")}
    </Button>,
    <Button
      key="submit"
      type={restProps.okButtonProps?.type || "primary"}
      danger={restProps.okButtonProps?.danger ?? false}
      disabled={restProps.okButtonProps?.disabled ?? false}
      loading={loading}
      onClick={handleSubmit}
    >
      {restProps.okText || t<string>("button.save")}
    </Button>,
  ], [onClose, loading, restProps.cancelText, restProps.okText, restProps.okButtonProps, t, handleSubmit]);

  const childrenWithController = useMemo(
    () => React.cloneElement(children, { formController }),
    [children, formController]
  );

  return (
    <Modal
      destroyOnClose
      afterClose={afterClose}
      width={700}
      maskClosable={false}
      open={open ?? visible}
      {...restProps}
      footer={footer}
    >
      <div className="py-3">{childrenWithController}</div>
    </Modal>
  );
}

export default ModalForm;
